import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { COLORS } from '../../constants/colors';

const DropdownContainer = styled.div`
  position: relative;
  min-width: 150px;
  user-select: none;
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 12px;
  background-color: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  color: #212529;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: ${COLORS.PRIMARY};
  }
  
  &:focus {
    outline: none;
    border-color: ${COLORS.PRIMARY};
    box-shadow: 0 0 0 3px rgba(66, 99, 235, 0.15);
  }
  
  svg {
    width: 16px;
    height: 16px;
    margin-left: 8px;
    color: #6c757d;
  }
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  background-color: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 10;
  max-height: 240px;
  overflow-y: auto;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const DropdownItem = styled.button<{ isSelected: boolean }>`
  display: block;
  width: 100%;
  padding: 8px 12px;
  text-align: left;
  background-color: ${props => props.isSelected ? '#f8f9fa' : 'transparent'};
  border: none;
  font-size: 14px;
  color: ${props => props.isSelected ? COLORS.PRIMARY : '#212529'};
  cursor: pointer;
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  &:focus {
    outline: none;
    background-color: #f8f9fa;
  }
`;

const FilterLabel = styled.div`
  font-size: 13px;
  color: #6c757d;
  margin-bottom: 4px;
`;

// Option interface
export interface DropdownOption {
  value: string;
  label: string;
}

// Props interface
export interface CustomDropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
  className?: string;
}

export const CustomDropdown: React.FC<CustomDropdownProps> = ({ 
  options, 
  value, 
  onChange, 
  label,
  className
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Find the selected option label
  const selectedOption = options.find(option => option.value === value);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  return (
    <DropdownContainer ref={dropdownRef} className={className}>
      {label && <FilterLabel>{label}</FilterLabel>}
      
      <DropdownButton 
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {selectedOption?.label || value}
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </DropdownButton>
      
      <DropdownMenu isOpen={isOpen} role="listbox">
        {options.map(option => (
          <DropdownItem
            key={option.value}
            isSelected={option.value === value}
            onClick={() => {
              onChange(option.value);
              setIsOpen(false);
            }}
            role="option"
            aria-selected={option.value === value}
          >
            {option.label}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </DropdownContainer>
  );
};

export default CustomDropdown;